import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Input,
  Button,
  Tabs,
  Row,
  Col,
  InputNumber,
  Switch,
  Select,
  Modal,
} from "antd";
import { PlusOutlined, ArrowLeftOutlined, EditOutlined, UploadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import QuestionCard, { QuestionData } from "../components/QuestionCard";
import TrainingResourceUploadModal from "../components/TrainingResourceUploadModal";
import QuestionsCsvUploadModal, { ParsedQuestion } from "../components/QuestionsCsvUploadModal";
import { createTest } from "../services/tests";
import theme from "../theme";
import { useNavigate, useParams } from "react-router-dom";
import { Test, Question, Expert } from "../types/industry";
import { getTest, updateTest } from "../services/industries";
import { listFunctions } from "../services/functions";
import { listRoles } from "../services/roles";
import { listSoftware } from "../services/software";
import { listTasks } from "../services/tasks";
import { toast } from "react-toastify";
import { useUser } from "../contexts/UserContext";
import api from "../services/api";
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import user_image from '../images/user_img.jpg'

/* helper to make a blank question */
const newQuestion = (): QuestionData => ({
  id: Date.now().toString(),
  prompt: "",
  answers: [
    { id: "a1", text: "" },
    { id: "a2", text: "" },
  ],
  function: undefined,
  task: undefined,
  role: undefined,
  difficulty: undefined,
  weight: 1,
  correctId: null,
  type: "multiple",
});

const TestBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { industryId, testId } = useParams();
  const { isSuperAdmin } = useUser();
  const isReadOnly = !isSuperAdmin();
  const [testName, setTestName] = useState("");
  const [testDescription, setTestDescription] = useState("");
  const [tab, setTab] = useState<"q" | "s">("q");
  const [questions, setQuestions] = useState<QuestionData[]>([newQuestion()]);
  const [settings, setSettings] = useState({
    time: 40,
    pass: 70,
    shuffle: true,
    showProgress: false,
    software: undefined as number | undefined,
  });
  const [expert, setExpert] = useState<Expert | undefined>();
  const [loading, setLoading] = useState(false);
  const [functions, setFunctions] = useState<Array<{ value: number; label: string }>>([]);
  const [roles, setRoles] = useState<Array<{ value: number; label: string }>>([]);
  const [software, setSoftware] = useState<Array<{ value: number; label: string }>>([]);
  const [tasks, setTasks] = useState<Array<{ value: number; label: string }>>([]);
  const [tasksByFunction, setTasksByFunction] = useState<Record<number, Array<{ value: number; label: string }>>>({});
  const [industryName, setIndustryName] = useState<string>('');
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [nameError, setNameError] = useState<string>("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedState, setLastSavedState] = useState<string>("");

  const [imageCropModalVisible, setImageCropModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showResourceUploadModal, setShowResourceUploadModal] = useState(false);
  const [showTaskWarningModal, setShowTaskWarningModal] = useState(false);
  const [questionsWithoutTasks, setQuestionsWithoutTasks] = useState<any[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showQuestionImportModal, setShowQuestionImportModal] = useState(false);
  const [questionImportSuccess, setQuestionImportSuccess] = useState(false);

  // Generate temporary test ID for new tests to allow resource uploads
  const [tempTestId] = useState(() => `temp_${Date.now()}`);
  const effectiveTestId = testId || tempTestId;

  const updateQuestion = (idx: number, q: QuestionData) => {
    setQuestions((prev) => prev.map((item, i) => (i === idx ? q : item)));
    setHasUnsavedChanges(true);
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, newQuestion()]);
    setHasUnsavedChanges(true);
  };

  const removeQuestion = (idx: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
    setHasUnsavedChanges(true);
  };

  // Function to get current form state as string for comparison
  const getCurrentFormState = useCallback(() => {
    return JSON.stringify({
      testName,
      testDescription,
      questions: questions.map(q => ({
        prompt: q.prompt,
        answers: q.answers,
        correctId: q.correctId,
        function: q.function,
        task: q.task,
        role: q.role,
        difficulty: q.difficulty,
        weight: q.weight
      })),
      settings
    });
  }, [testName, testDescription, questions, settings]);

  const handleTestNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTestName(e.target.value);
    setHasUnsavedChanges(true);
  };

  const handleTestDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTestDescription(e.target.value);
    setHasUnsavedChanges(true);
  };

  const handleBackClick = () => {
    const currentState = getCurrentFormState();
    const hasChanges = hasUnsavedChanges && currentState !== lastSavedState;

    if (hasChanges) {
      setShowBackConfirm(true);
    } else {
      // No unsaved changes, navigate directly
      navigate(-1);
    }
  };

  const confirmBack = () => {
    navigate(-1);
  };

  const cancelBack = () => {
    setShowBackConfirm(false);
  };

  const handleResourcesUploaded = (result: any) => {
    toast.success(`Successfully processed ${result.total_processed || 'training'} resources`);
    setShowResourceUploadModal(false);
    setUploadSuccess(true);
    // Refresh dropdown data after successful upload
    refreshData();
    // Success state will persist until next upload
  };

  const handleQuestionsImported = async (parsedQuestions: ParsedQuestion[]) => {
    const newQuestions: QuestionData[] = parsedQuestions.map((q) => {
      // Generate stable temporary IDs for each answer
      const answerIds = q.answers.map((_, i) => `imp_${Date.now()}_${i}_${Math.random()}`);
      const correctIdx = q.answers.findIndex((a) => a.is_correct);
      return {
        id: `imp_${Date.now()}_${Math.random()}`,
        prompt: q.prompt,
        answers: q.answers.map((a, i) => ({ id: answerIds[i], text: a.text })),
        correctId: correctIdx >= 0 ? answerIds[correctIdx] : null,
        function: q.function_id ?? undefined,
        task: q.task_id ?? undefined,
        role: q.role_id ?? undefined,
        difficulty: q.difficulty || undefined,
        weight: q.weight || 1,
        type: 'multiple' as const,
      };
    });

    const uniqueFunctionIds = Array.from(
      new Set(newQuestions.map((q) => q.function).filter((id): id is number => id !== undefined)),
    );

    // Fetch tasks for uncached functions directly (not via loadTasksForFunction which
    // calls setTasksByFunction internally and therefore can't be awaited for the state
    // update to be visible in the current closure).
    const uncachedIds = uniqueFunctionIds.filter((id) => !tasksByFunction[id]);
    const fetchResults = await Promise.all(
      uncachedIds.map(async (functionId) => {
        try {
          const tasksData = await listTasks(functionId);
          return {
            functionId,
            options: tasksData.map((t: any) => ({ value: t.id, label: t.name })) as Array<{ value: number; label: string }>,
          };
        } catch {
          return { functionId, options: [] as Array<{ value: number; label: string }> };
        }
      })
    );

    // Build the merged tasks map locally — available synchronously below.
    const mergedTasks: Record<number, Array<{ value: number; label: string }>> = { ...tasksByFunction };
    fetchResults.forEach(({ functionId, options }) => {
      mergedTasks[functionId] = options;
    });

    // Frontend-side fallback: for questions where backend returned task_id=null but
    // did return a task_name, try to match the name against the loaded task options.
    const resolvedQuestions = newQuestions.map((nq, i) => {
      if (nq.task !== undefined) return nq; // backend already resolved it
      const pq = parsedQuestions[i];
      if (!pq.task_name || !nq.function) return nq;
      const options = mergedTasks[nq.function] || [];
      const nameLower = pq.task_name.toLowerCase();
      const match = options.find((opt) => opt.label.toLowerCase() === nameLower);
      if (match) {
        return { ...nq, task: match.value };
      }
      return nq;
    });

    // Fire both state updates in the same synchronous block. React 18 batches them
    // into a single render so QuestionCard sees the task options AND the pre-selected
    // task value at the same time, allowing the Select to resolve the label correctly.
    setTasksByFunction(mergedTasks);
    setQuestions((prev) => {
      const hasOnlyBlank = prev.length === 1 && !prev[0].prompt.trim();
      return hasOnlyBlank ? resolvedQuestions : [...prev, ...resolvedQuestions];
    });

    setHasUnsavedChanges(true);
    setShowQuestionImportModal(false);
    setQuestionImportSuccess(true);
    toast.success(`${newQuestions.length} question(s) imported from CSV. Review and save the assessment.`);
  };

  // Validate industry exists when page loads
  const validateIndustry = async () => {
    if (!industryId) return;

    try {
      const response = await api.get(`/industries/${industryId}/`);
      setIndustryName(response.data.name);
    } catch (error: any) {
      console.error('Failed to validate industry:', error);

      // Handle different error scenarios
      if (error.response?.status === 404) {
        toast.error("Industry not found. Redirecting to industries list...");
        setTimeout(() => {
          navigate('/industries-list');
        }, 2000);
      } else if (error.response?.status === 403) {
        toast.error("You do not have permission to access this industry.");
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error("Network error. Please check your connection and try again.");
      } else {
        toast.error("Failed to validate industry. Please try again later.");
      }
    }
  };

  // Function to refresh functions, roles, and software data
  const refreshData = async () => {
    if (!industryId) return;

    try {
      const [functionsData, rolesData, softwareData] = await Promise.all([
        listFunctions(Number(industryId)),
        listRoles(Number(industryId)),
        listSoftware(Number(industryId)),
      ]);

      setFunctions(
        functionsData.map((f: any) => ({
          value: f.id,
          label: f.name,
        }))
      );

      setRoles(
        rolesData.map((r: any) => ({
          value: r.id,
          label: r.name,
        }))
      );

      setSoftware(
        softwareData.map((s: any) => ({
          value: s.id,
          label: s.name,
        }))
      );

      // Clear cached tasks to force reload when functions are selected
      setTasksByFunction({});

    } catch (error: any) {
      console.error("Failed to fetch functions/roles/software:", error);

      // Handle different error scenarios
      if (error.response?.status === 404) {
        toast.error("Industry not found. Redirecting to industries list...");
        setTimeout(() => {
          navigate('/industries-list');
        }, 2000);
      } else if (error.response?.status === 403) {
        toast.error("You do not have permission to access this industry.");
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error("Network error. Please check your connection and try again.");
      } else {
        toast.error("Failed to load industry data. Please try again later.");
      }

      // Set empty arrays to prevent UI crashes
      setFunctions([]);
      setRoles([]);
      setSoftware([]);
      setTasksByFunction({});
    }
  };

  const loadTasksForFunction = async (functionId: number) => {
    try {
      const tasksData = await listTasks(functionId);
      const tasksOptions = tasksData.map((t: any) => ({
        value: t.id,
        label: t.name,
      }));

      setTasksByFunction(prev => ({
        ...prev,
        [functionId]: tasksOptions
      }));
    } catch (error) {
      console.error("Failed to fetch tasks for function:", error);
    }
  }; const handleFunctionChange = (questionIndex: number, functionId: number | undefined) => {
    if (functionId && !tasksByFunction[functionId]) {
      loadTasksForFunction(functionId);
    }
  };

  const handleTaskWarningOk = () => {
    setShowTaskWarningModal(false);
    performSave();
  };

  const handleTaskWarningCancel = () => {
    setShowTaskWarningModal(false);
    toast.info("Use the 'Upload Resources' button to add tasks and improve PPP quality");
  };

  const save = async () => {
    if (!industryId) {
      toast.error("Industry ID missing in URL");
      return;
    }

    for (const q of questions) {
      if (!q.prompt.trim()) return toast.error("Each question needs text");
      if (q.answers.length < 2) return toast.error("Need at least 2 answers");
      if (!q.correctId) return toast.error("Select a correct answer");
      if (!q.function) return toast.error("Each question must have a Function selected");
    }

    // Check for missing tasks and warn user
    const questionsWithoutTasksCheck = questions.filter(q => q.function && !q.task);

    if (questionsWithoutTasksCheck.length > 0) {
      setQuestionsWithoutTasks(questionsWithoutTasksCheck);
      setShowTaskWarningModal(true);
      return;
    }

    // If all questions have tasks or no functions selected, proceed directly
    performSave();
  };

  const performSave = async () => {

    // Convert File objects to base64 for easier handling
    const processedQuestions = await Promise.all(
      questions.map(async (q) => {
        let imageData = q.image;

        // Convert File to base64 if it's a File object
        if (q.image && typeof q.image !== 'string') {
          const base64 = await fileToBase64(q.image);
          imageData = base64;
        }

        return {
          prompt: q.prompt,
          type: q.type,
          image: imageData,
          function_id: q.function,
          task_id: q.task,
          role_id: q.role,
          difficulty: q.difficulty,
          weight: q.weight,
          answers: q.answers.map((a) => ({
            text: a.text,
            is_correct: a.id === q.correctId,
          })),
        };
      })
    );

    const payload: Partial<Test> = {
      industry: Number(industryId),
      name: testName,
      description: testDescription,
      software: settings.software,
      time_limit: settings.time,
      passing_grade: settings.pass,
      shuffle_questions: settings.shuffle,
      show_progress: settings.showProgress,
      questions: processedQuestions as Question[],
      expert: expert
    };

    var isSuccess = false;
    var isUpdate = !!testId

    try {
      setLoading(true);
      setNameError(""); // Clear any previous errors

      if (isUpdate) {
        await updateTest(Number(testId), payload);
      } else {
        await createTest(payload);
      }
      toast.success("Assessment saved successfully!");
      setHasUnsavedChanges(false);
      setLastSavedState(getCurrentFormState());

      isSuccess = true;

    } catch (err: any) {
      console.error(err);

      // Handle validation errors from backend
      if (err.response?.status === 400 && err.response?.data) {
        const errorData = err.response.data;

        // Handle name validation error specifically
        if (errorData.name) {
          const nameErrorMsg = Array.isArray(errorData.name) ? errorData.name[0] : errorData.name;
          setNameError(nameErrorMsg);
          toast.error(nameErrorMsg);
        } else {
          // Handle other validation errors
          const firstError = Object.values(errorData)[0];
          const errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
          toast.error(errorMsg || "Validation failed. Please check your input.");
        }
      } else {
        toast.error("Save failed. Try again.");
      }
    } finally {
      setLoading(false);
    }

    if (isSuccess && !isUpdate) {
      navigate(`/view-industry/${industryId}`)
    }
  };

  // Helper function to convert File to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Expert image functions
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSelectedImage(event.target?.result as string);
      setImageCropModalVisible(true);
    };
    reader.readAsDataURL(file);
  };

  const getCroppedImg = (image: HTMLImageElement, crop: PixelCrop): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Canvas is empty');
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.8);
    });
  };

  const handleCropSave = async () => {
    if (!completedCrop || !imgRef.current) return;

    const croppedImageBase64 = await getCroppedImg(imgRef.current, completedCrop);
    setExpert({ ...expert, photo: croppedImageBase64 })

    setImageCropModalVisible(false);
    setSelectedImage(null);
  };

  const handleRemoveProfileImage = async () => {
    setExpert({ ...expert, photo: "" })
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;

    // Set crop to be a square in the center
    const size = Math.min(width, height);
    const crop: Crop = {
      unit: 'px',
      width: size * 0.8,
      height: size * 0.8,
      x: (width - size * 0.8) / 2,
      y: (height - size * 0.8) / 2,
    };

    const pixelCrop: PixelCrop = {
      unit: 'px',
      width: size * 0.8,
      height: size * 0.8,
      x: (width - size * 0.8) / 2,
      y: (height - size * 0.8) / 2,
    };

    setCrop(crop);
    setCompletedCrop(pixelCrop);
  };

  /* ----- tabs ----- */
  const questionsTab = (
    <>
      {questions.map((q, idx) => (
        <QuestionCard
          key={q.id}
          data={q}
          onChange={isReadOnly ? () => { } : (u) => updateQuestion(idx, u)}
          onRemove={isReadOnly ? undefined : () => removeQuestion(idx)}
          functions={functions}
          tasks={q.function ? tasksByFunction[q.function] || [] : []}
          roles={roles}
          readOnly={isReadOnly}
          onFunctionChange={(functionId) => handleFunctionChange(idx, functionId)}
        />
      ))}
      {!isReadOnly && (
        <Button
          icon={<PlusOutlined />}
          block
          type="dashed"
          onClick={addQuestion}
          style={addBtn}
        >
          Add another question
        </Button>
      )}
    </>
  );

  const settingsTab = (
    <div style={settingsBox}>
      <Row align="middle" gutter={8} style={{ marginBottom: 24 }}>
        <Col style={label}>Software</Col>
        <Col>
          <Select
            value={settings.software}
            placeholder="Select software"
            style={{ minWidth: 200 }}
            options={software}
            onChange={isReadOnly ? undefined : (v) => setSettings((s) => ({ ...s, software: v }))}
            allowClear
            disabled={isReadOnly}
          />
        </Col>
      </Row>
      <Row align="middle" gutter={8} style={{ marginBottom: 24 }}>
        <Col style={label}>Time limit</Col>
        <Col>
          <InputNumber
            min={1}
            max={300}
            value={settings.time}
            onChange={isReadOnly ? undefined : (v) => setSettings((s) => ({ ...s, time: v ?? s.time }))}
            style={numInput}
            disabled={isReadOnly}
          />
        </Col>
        <Col>minutes</Col>
      </Row>

      <Row align="middle" gutter={8} style={{ marginBottom: 24 }}>
        <Col style={label}>Passing grade</Col>
        <Col>
          <InputNumber
            min={1}
            max={100}
            value={settings.pass}
            onChange={isReadOnly ? undefined : (v) => setSettings((s) => ({ ...s, pass: v ?? s.pass }))}
            style={numInput}
            disabled={isReadOnly}
          />
        </Col>
        <Col>%</Col>
      </Row>

      <Row align="middle" gutter={12} style={{ marginBottom: 24 }}>
        <Col style={label}>Shuffle question order</Col>
        <Col>
          <Switch
            checked={settings.shuffle}
            onChange={isReadOnly ? undefined : (v) => setSettings((s) => ({ ...s, shuffle: v }))}
            disabled={isReadOnly}
          />
        </Col>
      </Row>

      <Row align="middle" gutter={12}>
        <Col style={label}>Show progress bar</Col>
        <Col>
          <Switch
            checked={settings.showProgress}
            onChange={isReadOnly ? undefined : (v) => setSettings((s) => ({ ...s, showProgress: v }))}
            disabled={isReadOnly}
          />
        </Col>
      </Row>
    </div>
  );

  const expertTab = (
    <div style={expertsBox}>
      <Row align="middle" gutter={8} style={{ marginBottom: 24 }}>
        <Col style={label}>Photo</Col>

        <Col>
          <div className="edit_icon d-flex justify-content-end mb-2">
            {!isReadOnly &&
              <EditOutlined
                onClick={() => fileInputRef.current?.click()}
                style={{ cursor: 'pointer', fontSize: '16px' }}
              />
            }
          </div>
          <img
            src={expert?.photo && expert.photo.trim() !== '' ? expert.photo : user_image}
            className="rounded-circle mb-3"
            alt="Profile"
            style={{ width: '120px', height: '120px', objectFit: 'cover' }}
            onError={(e) => {
              console.log('Image load error, falling back to default');
              e.currentTarget.src = expert?.photo ? expert.photo : "";
            }}
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            style={{ display: 'none' }}
          />
          <div>
            {!isReadOnly &&
              <button
                className="btn btn-outline-danger btn-sm"
                type="button"
                onClick={handleRemoveProfileImage}
                disabled={!expert?.photo}
              >
                Remove picture
              </button>
            }
          </div>
        </Col>
      </Row>

      <Row align="middle" gutter={8} style={{ marginBottom: 24 }}>
        <Col style={label}>Name</Col>
        <Col>
          <Input
            value={expert?.name}
            onChange={(v) => setExpert((s) => ({ ...s, name: v.target.value }))}
            style={nameInput}
            disabled={isReadOnly}
          />
        </Col>
      </Row>

      <Row align="middle" gutter={8} style={{ marginBottom: 24 }}>
        <Col style={label}>About</Col>
        <Col>
          <Input.TextArea
            rows={4}
            value={expert?.about}
            onChange={(v) => setExpert((s) => ({ ...s, about: v.target.value }))}
            style={{
              ...descriptionTextarea,
              borderRadius: 12,
              background: "#E9EDFF",
              border: "1px solid #d9d9d9",
              fontFamily: "inherit",
              fontSize: "14px",
              padding: "12px 16px",
              fontWeight: "bold"
            }}
            disabled={isReadOnly}
          />
        </Col>
      </Row>

      <Row align="middle" gutter={12} style={{ marginBottom: 24 }}>
        <Col style={label}>LinkedIn</Col>
        <Col>
          <Input
            value={expert?.linkedin}
            onChange={(v) => setExpert((s) => ({ ...s, linkedin: v.target.value }))}
            style={nameInput}
            disabled={isReadOnly}
          />
        </Col>
      </Row>
    </div>
  );

  // Validate industry on page load
  useEffect(() => {
    validateIndustry();
  }, [industryId]);

  // Fetch functions, roles, and software
  useEffect(() => {
    refreshData();
  }, [industryId]);

  useEffect(() => {
    if (!testId) return;
    (async () => {
      try {
        setLoading(true);
        const t: Test = await getTest(Number(testId));
        setTestName(t.name);
        setTestDescription(t.description || "");
        setSettings({
          time: t.time_limit,
          pass: t.passing_grade,
          shuffle: t.shuffle_questions,
          showProgress: t.show_progress,
          software: t.software,
        });
        setQuestions(
          t.questions!.map((q) => ({
            id: String(q.id),
            prompt: q.prompt,
            type: q.type as "multiple",
            image: q.image,
            function: q.function_fk_id,
            task: q.task_id_read,
            role: q.role_fk_id,
            difficulty: q.difficulty,
            weight: q.weight,
            answers: q.answers.map((a) => ({
              id: String(a.id),
              text: a.text,
            })),
            correctId: q.answers.find((a) => a.is_correct)
              ? String(q.answers.find((a) => a.is_correct)!.id) // ← cast to string
              : null,
          }))
        );
        setExpert({
          name: t.expert?.name,
          about: t.expert?.about,
          photo: t.expert?.photo,
          linkedin: t.expert?.linkedin,
        });

        // Load tasks for all functions that are already selected in questions
        const functionsWithTasks = new Set<number>();
        t.questions!.forEach((q) => {
          if (q.function_fk_id) {
            functionsWithTasks.add(q.function_fk_id);
          }
        });

        // Load tasks for each unique function and wait for all to complete
        const taskPromises = Array.from(functionsWithTasks).map(functionId => {
          return loadTasksForFunction(functionId);
        });

        // Wait for all task loading to complete
        await Promise.all(taskPromises);

        // Reset change tracking after loading
        setHasUnsavedChanges(false);
        // Set initial saved state after loading
        setTimeout(() => {
          setLastSavedState(getCurrentFormState());
        }, 0);
      } finally {
        setLoading(false);
      }
    })();
  }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={bg}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Row style={{ flex: 1, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <label style={{ marginRight: "20px" }}>Test name:</label>
            <Input
              value={testName}
              onChange={isReadOnly ? undefined : (e) => {
                handleTestNameChange(e);
                if (nameError) setNameError(""); // Clear error when user types
              }}
              style={{
                ...nameInput,
                borderColor: nameError ? '#ff4d4f' : undefined,
                boxShadow: nameError ? '0 0 0 2px rgba(255, 77, 79, 0.2)' : undefined
              }}
              status={nameError ? 'error' : undefined}
              readOnly={isReadOnly}
            />
            {nameError && (
              <div style={{
                color: '#ff4d4f',
                fontSize: '12px',
                marginTop: '4px',
                marginLeft: '20px'
              }}>
                {nameError}
              </div>
            )}
          </div>
        </Row>

        {!isReadOnly && (
          <div style={{ display: 'flex', gap: '12px', height: '40px' }}>
            <Button
              icon={questionImportSuccess ? <CheckCircleOutlined /> : <UploadOutlined />}
              onClick={() => setShowQuestionImportModal(true)}
              style={{
                background: questionImportSuccess ? '#389e0d' : '#1677ff',
                color: 'white',
                border: 'none',
                borderRadius: 9999,
                padding: '8px 20px',
                fontWeight: 600,
                height: '40px',
                transition: 'all 0.3s ease',
              }}
              title={questionImportSuccess ? 'Questions imported from CSV!' : 'Import questions from a CSV file'}
            >
              {questionImportSuccess ? 'Questions Imported!' : 'Import Questions'}
            </Button>
            <Button
              icon={uploadSuccess ? <CheckCircleOutlined /> : <UploadOutlined />}
              onClick={() => {
                setUploadSuccess(false); // Reset success state when starting new upload
                setShowResourceUploadModal(true);
              }}
              style={{
                background: uploadSuccess ? "#389e0d" : "#52c41a",
                color: "white",
                border: "none",
                borderRadius: 9999,
                padding: "8px 20px",
                fontWeight: 600,
                height: '40px',
                transition: 'all 0.3s ease'
              }}
              title={uploadSuccess ? "Resources uploaded successfully!" : "Upload Training Resources"}
            >
              {uploadSuccess ? "Resources Uploaded!" : "Upload Resources"}
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackClick}
              style={{
                borderRadius: 9999,
                padding: "8px 20px",
                fontWeight: 600,
                height: '40px'
              }}
            >
              Back
            </Button>
            <Button
              type="primary"
              style={btn}
              loading={loading}
              onClick={save}
              className="btn"
            >
              Save
            </Button>
          </div>
        )}
      </Row>

      {/* Test Description Field */}
      <div style={{ marginBottom: 32, width: '100%' }}>
        <label style={{
          marginRight: "20px",
          marginBottom: "12px",
          display: "block",
          fontSize: "14px"
        }}>
          Description:
        </label>
        <Input.TextArea
          value={testDescription}
          onChange={isReadOnly ? undefined : handleTestDescriptionChange}
          placeholder="Enter assessment description..."
          style={{
            ...descriptionTextarea,
            borderRadius: 12,
            background: "#E9EDFF",
            border: "1px solid #d9d9d9",
            fontFamily: "inherit",
            fontSize: "14px",
            padding: "12px 16px"
          }}
          rows={4}
          readOnly={isReadOnly}
        />
      </div>

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as any)}
        items={[
          { key: "q", label: "Questions", children: questionsTab },
          { key: "s", label: "Settings", children: settingsTab },
          { key: "e", label: "Experts", children: expertTab }
        ]}
      />

      {/* Back Confirmation Modal */}
      <Modal
        open={showBackConfirm}
        title="Discard Changes?"
        onCancel={cancelBack}
        footer={[
          <Button key="cancel" onClick={cancelBack} style={{
            borderRadius: 9999,
            padding: "8px 20px",
            fontWeight: 600,
            height: '40px'
          }}>
            Cancel
          </Button>,
          <Button key="discard" type="primary" danger onClick={confirmBack}>
            Discard Changes
          </Button>,
        ]}
        centered
      >
        <p>Are you sure you want to go back? All unsaved changes will be discarded.</p>
      </Modal>

      {/* Image Crop Modal */}
      <Modal
        title="Crop Expert Image"
        className="input-form"
        open={imageCropModalVisible}
        onCancel={() => {
          setImageCropModalVisible(false);
          setSelectedImage(null);
        }}
        width={600}
        footer={[
          <button
            key="cancel"
            className="btn btn-secondary me-2"
            onClick={() => {
              setImageCropModalVisible(false);
              setSelectedImage(null);
            }}
          >
            Cancel
          </button>,
          <button
            key="save"
            className="btn btn-primary"
            onClick={handleCropSave}
            disabled={!completedCrop}
          >
            Save Image
          </button>
        ]}
      >
        {selectedImage && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1} // Force square crop
              circularCrop={true}
            >
              <img
                ref={imgRef}
                src={selectedImage}
                alt="Crop preview"
                onLoad={onImageLoad}
                style={{ maxWidth: '100%', maxHeight: '400px' }}
              />
            </ReactCrop>
          </div>
        )}
      </Modal>

      {/* Task Warning Modal */}
      <Modal
        open={showTaskWarningModal}
        title="Questions Missing Tasks"
        onCancel={handleTaskWarningCancel}
        footer={[
          <Button key="cancel" onClick={handleTaskWarningCancel} style={{
            borderRadius: 9999,
            padding: "8px 20px",
            fontWeight: 600,
            height: '40px'
          }}>
            Cancel & Add Tasks
          </Button>,
          <Button key="save" type="primary" onClick={handleTaskWarningOk} style={{
            borderRadius: 9999,
            padding: "8px 20px",
            fontWeight: 600,
            height: '40px'
          }}>
            Save Anyway
          </Button>,
        ]}
        centered
      >
        <div>
          <p><strong>{questionsWithoutTasks.length}</strong> question(s) don't have Tasks assigned.</p>
          <p>Tasks help generate more specific and targeted Personalised Proficiency Plans (PPP).</p>
          <p><strong>You can add tasks by:</strong></p>
          <ul>
            <li>Using the "Upload Resources" button to upload training materials with task definitions</li>
            <li>Or proceed without tasks (PPP will contain more generic content)</li>
          </ul>
          <p>Do you want to proceed with saving?</p>
        </div>
      </Modal>

      {/* Training Resource Upload Modal */}
      <TrainingResourceUploadModal
        visible={showResourceUploadModal}
        onClose={() => setShowResourceUploadModal(false)}
        testId={effectiveTestId}
        industryId={Number(industryId)}
        industryName={industryName}
        isTemporaryTest={!testId}
        onUploadSuccess={handleResourcesUploaded}
      />

      {/* Questions CSV Import Modal */}
      <QuestionsCsvUploadModal
        visible={showQuestionImportModal}
        onClose={() => setShowQuestionImportModal(false)}
        industryId={Number(industryId)}
        industryName={industryName}
        onQuestionsImported={handleQuestionsImported}
      />
    </div>
  );
};

const bg: React.CSSProperties = {
  minHeight: "100vh",
};

const nameInput: React.CSSProperties = {
  maxWidth: 400,
  borderRadius: 9999,
  background: "#E9EDFF",
  fontWeight: 700,
};

const addBtn: React.CSSProperties = {
  background: "#E9EDFF",
  borderRadius: 12,
  marginTop: 12,
};

const settingsBox: React.CSSProperties = {
  background: "#ffffff",
  borderRadius: 12,
  padding: 40,
  maxWidth: 480,
};

const expertsBox: React.CSSProperties = {
  background: "#ffffff",
  borderRadius: 12,
  padding: 40,
  maxWidth: 480,
};

const label: React.CSSProperties = { minWidth: 160, fontWeight: 600 };

const numInput: React.CSSProperties = {
  width: 80,
  borderRadius: 9999,
  background: "#E9EDFF",
};

const btn: React.CSSProperties = {
  background: theme.colors.primary,
  borderRadius: 9999,
  border: "none",
  fontWeight: 600,
  width: "100px",
  display: "flex",
  color: "white"
};

const descriptionTextarea: React.CSSProperties = {
  width: "100%",
  minHeight: "80px",
  fontSize: "14px",
  fontWeight: 400,
};

export default TestBuilder;
